import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import {
    Trash,
    Create,
    VideoCam,
    Image,
    HeadsetIcon,
    ArrowUpward,
    ArrowDownward,
    PreviewIcon,
} from '../../components/Icons/Icon';
import AlertDialog from '../../components/Dialog/AlertDialog';
import Button from '../../components/Button/CustomButton';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Pagination from '../../components/Pagination/Pagination';
import Snackbar from '../../components/Snackbar/Snackbar';
import Table from '../../components/Table/Table';
import Typography from '../../components/Typography/CustomTypography';
import WrapperContainer from '../../components/Container/WrapperContainer';
import ProductRegister from '../../containers/productRegister/ProductRegister';
import PRODUCT_DIALOG_MODE from './Consts';
import styles from '../../assets/jss/pages/productManagementPageStyle';
import Loader from '../../components/Loader/Loader';
import { BOOTH_TYPE, MEDIA_TYPE } from '../../common/Constants';

import { convertDuration } from '../../utils/dateUtils';
import CustomTextSelector from '../../selectors/CustomText';

const useStyles = makeStyles(styles);

const ProductManagement = ({
    eventInfo, companyID, companyDetail, productList: products, isLoading, fetchInfo, clearInfo, fetchList, clearList,
    deleteProduct, deleteProductClear, successDelete, errorDelete,
    updateProductOrder, updateOrderSuccess, updateOrderError, clearUpdateProductOrder,
}) => {
    const PAGE_SIZE = 20;
    const PULL_ACTION = {
        UP: 1,
        DOWN: 0,
    };
    const params = useParams();
    const classes = useStyles();
    const history = useHistory();
    const [isOpenDialogDelete, setOpenDialogDelete] = useState({ isOpen: false, waitingOrder: false });
    const [isOpenProductDialog, setOpenProductDialog] = useState(false);
    const [isMaxLength, setIsMaxLength] = useState(false);
    const [productDialogMode, setProductDialogMode] = useState();
    const [errorDialog, setErrorDialog] = useState({ isOpen: false, message: '' });
    const [productID, setProductID] = useState();

    const [reOrderMode, setReOrderMode] = useState(false);
    const [reOrderBlocks, setReOrderBlocks] = useState({});
    const [reOrderList, setReOrderList] = useState([]);
    const [reOrderIndexList, setReOrderIndexList] = useState([]);
    const customText = useSelector(CustomTextSelector.createGetCustomText());

    const indexPage = useRef(null);
    const activeIndex = useRef(null);
    const pullAction = useRef(null);
    const relatedPage = useRef(null);

    const intl = useIntl();

    const isNeedFetchProducts = () => {
        if (pullAction.current === PULL_ACTION.UP && activeIndex.current % PAGE_SIZE === 0) {
            if (indexPage.current > 0 && _.isUndefined(reOrderBlocks[`${indexPage.current - 1}`])) return true;
        }

        if (pullAction.current === PULL_ACTION.DOWN && (activeIndex.current + 1) % PAGE_SIZE === 0) {
            if (indexPage.current < products.total_page && _.isUndefined(reOrderBlocks[`${indexPage.current + 1}`])) return true;
        }

        return false;
    };

    const getRelatedPage = (page) => {
        if (pullAction.current === PULL_ACTION.UP && activeIndex.current % PAGE_SIZE === 0) {
            return page - 1;
        }

        if (pullAction.current === PULL_ACTION.DOWN && (activeIndex.current + 1) % PAGE_SIZE === 0) {
            return page + 1;
        }

        return page;
    };

    const createReOrderList = (page) => {
        let startIndex = (page - 1) * PAGE_SIZE;
        let finishIndex = (page - 1) * PAGE_SIZE + reOrderBlocks[`${page}`].length;

        let newReOrderData = [...reOrderIndexList].slice(startIndex, finishIndex).map(item => {
            return reOrderBlocks[`${Math.floor(item / PAGE_SIZE) + 1}`][item % PAGE_SIZE];
        });

        setReOrderList(generateTableData(newReOrderData, page));
    };

    const createReOrderIndexList = (index, pullAction) => {
        let tempIndexList = !reOrderIndexList.length
            ? [...Array(products.total_length).keys()]
            : [...reOrderIndexList];

        if (pullAction === PULL_ACTION.UP) {
            [tempIndexList[index], tempIndexList[index - 1]] = [tempIndexList[index - 1], tempIndexList[index]];
        }

        if (pullAction === PULL_ACTION.DOWN) {
            [tempIndexList[index], tempIndexList[index + 1]] = [tempIndexList[index + 1], tempIndexList[index]];
        }

        setReOrderIndexList(tempIndexList);
    };

    const handlePullAction = (e, index, action) => {
        e.stopPropagation();
        if (!reOrderMode) setReOrderMode(true);

        activeIndex.current = index;
        pullAction.current = action;

        createReOrderIndexList(index, action);
    };

    const handleChangePage = (page) => {
        if (!reOrderMode) {
            fetchList(companyID, { page });
        } else {
            if (_.isUndefined(reOrderBlocks[`${page}`])) {
                relatedPage.current = products.page;
                fetchList(companyID, { page });
            } else {
                indexPage.current = page;
                createReOrderList(page);
            }
        }
    };

    const clearReOrderState = () => {
        setReOrderMode(false);
        setReOrderBlocks({});
        setReOrderList([]);
        setReOrderIndexList([]);

        pullAction.current = null;
        activeIndex.current = null;
        indexPage.current = null;
        relatedPage.current = null;
    };

    const cancelReOrderProductList = () => {
        clearReOrderState();
        fetchList(companyID, {page: 1});
    };

    const applyReOrderProductList = () => {
        let objSeq = {
            ob_seq: [],
        };

        for (let i = 0, length = reOrderIndexList.length; i < length; i++) {
            if (i !== reOrderIndexList[i]) {
                let newSeq = {
                    "product_id": reOrderBlocks[`${Math.floor(reOrderIndexList[i] / PAGE_SIZE) + 1}`][reOrderIndexList[i] % PAGE_SIZE].id,
                    "seq_update": reOrderBlocks[`${Math.floor(i / PAGE_SIZE) + 1}`][i % PAGE_SIZE].product_seq,
                };

                objSeq.ob_seq.push(newSeq);
            }
        }

        updateProductOrder(companyID, objSeq);
    };

    const generateTableData = (data, page) => {
        const tableData = companyDetail.organizerBooth === BOOTH_TYPE.GRAND
            ? data.map((product, index) => [
                product.name,
                product.description,
                `${intl.formatNumber(product.price)}円`,
                renderMediaType(product.media_type),
                product.media_type === MEDIA_TYPE.VIDEO || product.media_type === MEDIA_TYPE.AUDIO
                    ? convertDuration(
                        product.media_duration,
                        intl.formatMessage({ id: 'common.hour' }),
                        intl.formatMessage({ id: 'common.minute' }),
                        intl.formatMessage({ id: 'common.second' }),
                    ) : '',
                <ActionRow
                    onDelete={(e) => handleClickDeleteProduct(e, product.id, product.is_in_order_waiting)}
                    onEdit={(e) => handleClickUpdateProduct(e, product.id)}
                />,
                <SortAction
                    pullUp={(e) => handlePullAction(e, (page - 1) * PAGE_SIZE + index, PULL_ACTION.UP)}
                    pullDown={(e) => handlePullAction(e, (page - 1) * PAGE_SIZE + index, PULL_ACTION.DOWN)}
                    isBegin={page === 1 && index === 0}
                    isEnd={(page - 1) * PAGE_SIZE + index === products.total_length - 1}
                />,
            ])
            : data.map((product, index) => [
                product.name,
                product.description,
                renderMediaType(product.media_type),
                product.media_type === MEDIA_TYPE.VIDEO || product.media_type === MEDIA_TYPE.AUDIO
                    ? convertDuration(
                        product.media_duration,
                        intl.formatMessage({ id: 'common.hour' }),
                        intl.formatMessage({ id: 'common.minute' }),
                        intl.formatMessage({ id: 'common.second' }),
                    ) : '',
                <ActionRow
                    onDelete={(e) => handleClickDeleteProduct(e, product.id, product.is_in_order_waiting)}
                    onEdit={(e) => handleClickUpdateProduct(e, product.id)}
                />,
                <SortAction
                    pullUp={(e) => handlePullAction(e, (page - 1) * PAGE_SIZE + index, PULL_ACTION.UP)}
                    pullDown={(e) => handlePullAction(e, (page - 1) * PAGE_SIZE + index, PULL_ACTION.DOWN)}
                    isBegin={page === 1 && index === 0}
                    isEnd={(page - 1) * PAGE_SIZE + index === products.total_length - 1}
                />,
            ]);

        return tableData;
    };

    useEffect(() => {
        if (Array.isArray(products.results) && !_.isEmpty(companyDetail)) {
            indexPage.current = products.page;

            if (reOrderMode) {
                if (_.isUndefined(reOrderBlocks[`${products.page}`])) {
                    setReOrderBlocks({
                        ...reOrderBlocks,
                        [`${products.page}`]: products.results,
                    });
                }
            } else {
                setReOrderBlocks({
                    ...reOrderBlocks,
                    [`${products.page}`]: products.results,
                });

                setReOrderList(generateTableData(products.results, products.page));
            }
        }
    }, [products, companyDetail]);

    useEffect(() => {
        if (_.isEmpty(reOrderBlocks)) return;

        if (!_.isUndefined(reOrderBlocks[`${indexPage.current}`]) && !_.isUndefined(reOrderBlocks[`${relatedPage.current}`])) {
            createReOrderList(indexPage.current);
        }
    }, [reOrderBlocks]);

    useEffect(() => {
        if (!reOrderIndexList.length) return;

        if (isNeedFetchProducts()) {
            relatedPage.current = getRelatedPage(products.page);

            if (_.isUndefined(reOrderBlocks[`${products.page}`])) {
                setReOrderBlocks({
                    ...reOrderBlocks,
                    [`${products.page}`]: products.results,
                });
            }

            fetchList(companyID, {page: getRelatedPage(products.page)});
        } else {
            let nextPage = getRelatedPage(indexPage.current);
            indexPage.current = nextPage;

            if (_.isUndefined(reOrderBlocks[`${nextPage}`])) {
                relatedPage.current = nextPage;
                setReOrderBlocks({
                    ...reOrderBlocks,
                    [`${nextPage}`]: products.results,
                });
            } else {
                createReOrderList(nextPage);
            }
        }
    }, [reOrderIndexList]);

    useEffect(() => {
        if (updateOrderSuccess === true) {
            clearReOrderState();
            fetchList(companyID);

            setErrorDialog({
                isOpen: true,
                severity: 'success',
                message: intl.formatMessage({ id: 'apiSuccess.update' }),
            });

            clearUpdateProductOrder();
        }

        if (!_.isEmpty(updateOrderError) && updateOrderError?.non_field_errors?.message) {
            setErrorDialog({
                isOpen: true,
                severity: 'error',
                message: updateOrderError?.non_field_errors?.message,
            });

            clearUpdateProductOrder();
        }
    }, [updateOrderSuccess, updateOrderError, fetchList, clearUpdateProductOrder, companyID]);

    // =======================================

    useEffect(() => {
        fetchInfo();
        return () => clearInfo();
    }, [fetchInfo, clearInfo]);

    useEffect(() => {
        if (companyID) fetchList(companyID);
        return () => { if (companyID) clearList(); };
    }, [fetchList, clearList, companyID]);

    useEffect(() => {
        if (eventInfo.setting_booth && companyDetail.organizerBooth) {
            let settings = {};
            switch (companyDetail.organizerBooth) {
                case 1:
                    settings = eventInfo.setting_booth.small_zone;
                    break;
                case 2:
                    settings = eventInfo.setting_booth.standard_zone;
                    break;
                case 3:
                    settings = eventInfo.setting_booth.big_zone;
                    break;
                case 4:
                    settings = eventInfo.setting_booth.grand_zone;
                    break;
                default:
                    settings = null;
            }
            if (settings?.total_media === products.total_length) {
                setIsMaxLength(true);
            } else {
                setIsMaxLength(false);
            }
        }
    }, [eventInfo.setting_booth, products.total_length, companyDetail.organizerBooth]);

    useEffect(() => {
        if (successDelete !== undefined) {
            fetchList(companyID);
            if (successDelete === false) {
                setErrorDialog({
                    isOpen: true,
                    severity: 'error',
                    message: errorDelete.message.message,
                });
            }
            deleteProductClear();
        }
    }, [successDelete, errorDelete, fetchList, deleteProductClear, companyID]);

    const handleCloseErrorDialog = () => setErrorDialog({ ...errorDialog, isOpen: false });

    const handleCloseDialogDelete = () => setOpenDialogDelete({ isOpen: false, waitingOrder: false });

    const handleCloseProductDialog = () => setOpenProductDialog(false);

    const handleClickViewProduct = (index) => {
        setProductDialogMode(PRODUCT_DIALOG_MODE.READ);
        setProductID(products.results[index].id);
        setOpenProductDialog(true);
    };

    const handleClickRegisterProduct = () => {
        setProductDialogMode(PRODUCT_DIALOG_MODE.CREATE);
        setOpenProductDialog(true);
    };

    const handleClickPreview = () => {
        history.push(`/${params.shortName}/product-management/${companyID}?is_preview=true`);
    };

    const handleClickUpdateProduct = (e, id) => {
        e.stopPropagation(); // Prevent Table onClick from activating
        setProductDialogMode(PRODUCT_DIALOG_MODE.UPDATE);
        setProductID(id);
        setOpenProductDialog(true);
    };

    const handleClickDeleteProduct = (e, id, orderWaiting) => {
        e.stopPropagation();
        setProductID(id);
        setOpenDialogDelete({ isOpen: true, waitingOrder: orderWaiting });
    };

    const contentDelete = (waiting) => (
        <>
            <FormattedMessage id="productManagement.confirmDeleteMessage" />
            { waiting && (
                <>
                    <p>この商品が購入されていますが、承認されていません。</p>
                    <p>名前、価格を変更したり、商品を削除したりする場合、この商品を含む注文が承認されなくなります。</p>
                </>
            )}
        </>
    );

    const handleDeleteProduct = () => {
        deleteProduct(companyID, productID);
        setOpenDialogDelete({ isOpen: false, waitingOrder: false });
    };

    const handleCloseCreateEditDialog = () => {
        handleCloseProductDialog();
        fetchList(companyID);
    };

    const handleClickReadToUpdateProduct = () => setProductDialogMode(PRODUCT_DIALOG_MODE.UPDATE);

    const renderMediaType = (type) => (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexWrap="wrap">
            {type === MEDIA_TYPE.IMAGE && <Image color="black" />}
            {type === MEDIA_TYPE.AUDIO && <HeadsetIcon color="black" />}
            {type === MEDIA_TYPE.VIDEO && <VideoCam color="black" />}
        </Box>
    );

    const renderBoothType = () => {
        switch (companyDetail.organizerBooth) {
            case 1:
                return _.get(eventInfo, 'setting_booth.small_zone.name');
            case 2:
                return _.get(eventInfo, 'setting_booth.standard_zone.name');
            case 3:
                return _.get(eventInfo, 'setting_booth.big_zone.name');
            case 4:
                return _.get(eventInfo, 'setting_booth.grand_zone.name');
            default:
                return null;
        }
    };

    const renderBoothSetting = () => {
        if (eventInfo.setting_booth && companyDetail.organizerBooth) {
            let settings = {};
            switch (companyDetail.organizerBooth) {
                case 1:
                    settings = eventInfo.setting_booth.small_zone;
                    break;
                case 2:
                    settings = eventInfo.setting_booth.standard_zone;
                    break;
                case 3:
                    settings = eventInfo.setting_booth.big_zone;
                    break;
                case 4:
                    settings = eventInfo.setting_booth.grand_zone;
                    break;
                default:
                    return null;
            }

            return (
                <ul>
                    <li>
                        {intl.formatMessage({ id: 'image' })}
                        ：
                        {!_.isEmpty(settings) && settings.image}
                        {intl.formatMessage({ id: 'productManagement.upToSheet' })}
                    </li>
                    <li>
                        {intl.formatMessage({ id: 'productManagement.imageTitle' })}
                    </li>
                    <li>
                        {intl.formatMessage({ id: 'productManagement.productDescription' })}
                    </li>
                    <li>
                        {intl.formatMessage({ id: 'audio' })}
                        ：
                        {!_.isEmpty(settings) && settings.audio}
                        {intl.formatMessage({ id: 'productManagement.upToItem' })}
                    </li>
                    <li>
                        {intl.formatMessage({ id: 'video' })}
                        ：
                        {!_.isEmpty(settings) && settings.video}
                        {intl.formatMessage({ id: 'productManagement.upToItem' })}
                    </li>
                </ul>
            );
        }
        return null;
    };

    const renderBoothTotalMedia = () => {
        if (eventInfo.setting_booth && companyDetail.organizerBooth) {
            let settings = {};
            switch (companyDetail.organizerBooth) {
                case 1:
                    settings = eventInfo.setting_booth.small_zone;
                    break;
                case 2:
                    settings = eventInfo.setting_booth.standard_zone;
                    break;
                case 3:
                    settings = eventInfo.setting_booth.big_zone;
                    break;
                case 4:
                    settings = eventInfo.setting_booth.grand_zone;
                    break;
                default:
                    return null;
            }
            return (
                <li className={classes.totalNotes}>
                    {intl.formatMessage({ id: 'productManagement.totalImageSound' })}
                    ：
                    {!_.isEmpty(settings) && settings.total_media}
                    {intl.formatMessage({ id: 'productManagement.item_suffix' })}
                    <br />
                    {intl.formatMessage({ id: 'productManagement.totalImageSoundNote' })}
                </li>
            );
        }
        return null;
    };

    const renderProductDialog = () => {
        if (!productDialogMode) return null;
        let mode;
        let product;
        let dialogTitle;
        let onClickChangeToEditDialog;
        let closeDialogFunction;

        switch (productDialogMode) {
            case PRODUCT_DIALOG_MODE.READ:
                mode = PRODUCT_DIALOG_MODE.READ;
                product = productID;
                onClickChangeToEditDialog = handleClickReadToUpdateProduct;
                dialogTitle = customText?.content?.screen_name?.exhibit_product_details || intl.formatMessage({ id: 'productDetails.pageTitle' });
                break;
            case PRODUCT_DIALOG_MODE.CREATE:
                mode = PRODUCT_DIALOG_MODE.CREATE;
                closeDialogFunction = handleCloseCreateEditDialog;
                dialogTitle = customText?.content?.screen_name?.exhibition_product_registration || intl.formatMessage({ id: 'productRegister.pageTitle' });
                break;
            case PRODUCT_DIALOG_MODE.UPDATE:
                mode = PRODUCT_DIALOG_MODE.UPDATE;
                product = productID;
                closeDialogFunction = handleCloseCreateEditDialog;
                dialogTitle = customText?.content?.screen_name?.exhibition_product_editing || intl.formatMessage({ id: 'productUpdate.pageTitle' });
                break;
            default:
                dialogTitle = null;
        }

        return (
            <ProductRegister
                mode={mode}
                productID={product}
                onClickChangeToEditDialog={onClickChangeToEditDialog}
                closeDialogFunction={closeDialogFunction}
                isOpenProductDialog={isOpenProductDialog}
                handleCloseProductDialog={handleCloseProductDialog}
                dialogTitle={dialogTitle}
                companyDetail={companyDetail}
            />
        );
    };

    const tableHead = companyDetail.organizerBooth === BOOTH_TYPE.GRAND
        ? [
            <>{customText?.content?.field_name?.product_name || '商品名'}</>,
            <>{customText?.content?.field_name?.explanation || '説明'}</>,
            <>{customText?.content?.field_name?.price || '料金'}</>,
            <>{customText?.content?.field_name?.media_type || 'メディアタイプ'}</>,
            <>{customText?.content?.field_name?.playback_time || '動画・音声・画像（再生時間）'}</>,
            <>{customText?.content?.field_name?.action || 'アクション'}</>,
            <>{customText?.content?.field_name?.sort || 'ソート'}</>,
        ]
        : [
            <>{customText?.content?.field_name?.product_name || '商品名'}</>,
            <>{customText?.content?.field_name?.explanation || '説明'}</>,
            <>{customText?.content?.field_name?.media_type || 'メディアタイプ'}</>,
            <>{customText?.content?.field_name?.playback_time || '動画・音声・画像（再生時間）'}</>,
            <>{customText?.content?.field_name?.action || 'アクション'}</>,
            <>{customText?.content?.field_name?.sort || 'ソート'}</>,
        ];

    const tableHeadWidth = companyDetail.organizerBooth === BOOTH_TYPE.GRAND
        ? ['15%', '25%', '10%', '10%', '10%', '15%', '15%']
        : ['15%', '35%', '10%', '10%', '15%', '15%'];

    return (
        <>
            <WrapperContainer className={classes.wrapper}>
                <GridItem xs={12}>
                    <Box paddingBottom={4} paddingTop={4}>
                        <GridContainer>
                            <GridItem align="center">
                                <Typography bold variant="h4" color="info">
                                    {customText?.content?.screen_name?.exhibit_product_management || intl.formatMessage({ id: 'productManagement.pageTitle' })}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </Box>
                    <Typography bold>
                        <ul>
                            <li>{intl.formatMessage({ id: 'organizer' })}</li>
                            <ul>
                                <li>
                                    {customText?.content_company?.field_name?.exhibitor_name || intl.formatMessage({ id: 'companyName' })}
                                    ：
                                    {companyDetail.fullName}
                                </li>
                                <li>
                                    {intl.formatMessage({ id: 'representative' })}
                                    ：
                                    {!!(companyDetail.firstName && companyDetail.lastName)
                                    && `${companyDetail.firstName} ${companyDetail.lastName}`}
                                </li>
                            </ul>
                            <li>
                                {intl.formatMessage({ id: 'organizerBooth' })}
                                ：
                                {renderBoothType()}
                                {renderBoothSetting()}
                            </li>
                            {renderBoothTotalMedia()}
                        </ul>
                    </Typography>
                    <div className={classes.listButton}>
                        <Button disabled={isMaxLength} style={{ fontSize: '1.75rem' }} color="info" size="lg" onClick={handleClickRegisterProduct}>
                            {customText?.content?.button_name?.exhibitor_registration_work || intl.formatMessage({ id: 'registerProduct' })}
                        </Button>
                        <Button style={{ fontSize: '1.75rem' }} color="danger" className={classes.buttonView} onClick={handleClickPreview} disabled={_.isEmpty(products?.results)}>
                            {customText?.content?.button_name?.exhibitor_preview || 'プレビュー'}
                            <span style={{ marginLeft: '10px' }}><PreviewIcon size="30px" color="#fff" /></span>
                        </Button>
                    </div>

                    {reOrderMode && (
                        <div className={classes.reOrderActionsWrapper}>
                            <Button
                                gradient
                                size="md"
                                color="warning"
                                className={classes.reOrderCancelBtn}
                                onClick={cancelReOrderProductList}
                            >
                                {intl.formatMessage({ id: 'cancel' })}
                            </Button>
                            <Button
                                gradient
                                size="md"
                                color="success"
                                className={classes.reOrderApplyBtn}
                                onClick={applyReOrderProductList}
                            >
                                {intl.formatMessage({ id: 'apply' })}
                            </Button>
                        </div>
                    )}

                    <Table
                        onClick={handleClickViewProduct}
                        tableHead={tableHead}
                        tableHeadWidth={tableHeadWidth}
                        tableData={reOrderList}
                        tableFooter={(
                            <Box display="flex" justifyContent="center">
                                <Pagination
                                    page={indexPage.current}
                                    total={products.total_page}
                                    onChange={(page) => handleChangePage(page)}
                                />
                            </Box>
                        )}
                        noDataMessage={(
                            <GridItem
                                xs={12}
                                style={{
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '20px',
                                    zIndex: 1,
                                }}
                            >
                                <FormattedMessage id="productManagement.noData" />
                            </GridItem>
                        )}
                    />
                </GridItem>
            </WrapperContainer>
            <AlertDialog
                title={<FormattedMessage id="dialog.confirmDeleteTitle" />}
                handleClose={handleCloseDialogDelete}
                handleOpen={isOpenDialogDelete.isOpen}
                handleSubmit={handleDeleteProduct}
                contents={contentDelete(isOpenDialogDelete.waitingOrder)}
            />
            {renderProductDialog()}
            <Snackbar
                open={errorDialog.isOpen}
                onClose={handleCloseErrorDialog}
                message={errorDialog.message}
                severity={errorDialog.severity}
            />
            <Loader isLoading={isLoading} />
        </>
    );
};

const SortAction = ({
    pullUp, pullDown, isBegin, isEnd,
}) => (
    <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap">
        <IconButton onClick={pullUp} disabled={isBegin}>
            <ArrowUpward />
        </IconButton>
        <IconButton onClick={pullDown} disabled={isEnd}>
            <ArrowDownward />
        </IconButton>
    </Box>
);

SortAction.propTypes = {
    pullUp: PropTypes.func.isRequired,
    pullDown: PropTypes.func.isRequired,
    isBegin: PropTypes.bool.isRequired,
    isEnd: PropTypes.bool.isRequired,
};

const ActionRow = ({ onEdit, onDelete }) => (
    <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap">
        <IconButton onClick={onDelete}>
            <Trash />
        </IconButton>
        <IconButton onClick={onEdit}>
            <Create />
        </IconButton>
    </Box>
);

ActionRow.propTypes = {
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

ProductManagement.defaultProps = {
    eventInfo: {},
    companyID: '',
    isLoading: false,
    productList: {},
    successDelete: undefined,
    errorDelete: {},
    updateOrderError: {},
};

ProductManagement.propTypes = {
    eventInfo: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.object,
    ])),
    companyID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    companyDetail: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ])).isRequired,
    isLoading: PropTypes.bool,
    productList: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.array,
        PropTypes.object,
    ])),
    fetchInfo: PropTypes.func.isRequired,
    clearInfo: PropTypes.func.isRequired,
    fetchList: PropTypes.func.isRequired,
    clearList: PropTypes.func.isRequired,
    deleteProduct: PropTypes.func.isRequired,
    deleteProductClear: PropTypes.func.isRequired,
    successDelete: PropTypes.bool,
    errorDelete: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ])),
    updateOrderError: PropTypes.objectOf(PropTypes.oneOfType([() => null, PropTypes.object])),
};

export default ProductManagement;
